/**
 * Admin project detail page for /admin/workspaces/:workspaceId/projects/:projectId route.
 * Provides tabbed interface for managing a specific project.
 */

import { useState } from "react";
import { useParams, Navigate, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../components/layout/PageHeader";
import { useProject } from "../../hooks/useProjects";
import { useProfile } from "../../contexts/ProfileContext";
import { useDateFormatter } from "../../hooks/useDateFormatter";
import { AdminProjectCollaboratorsList } from "./AdminProjectCollaboratorsList";
import {
  AlertCircle,
  Settings,
  Users,
  ArrowLeft,
  FileText,
  Calendar,
} from "lucide-react";
import { publicRoutes } from "../../lib/routes";

type TabId = "settings" | "collaborators";

interface Tab {
  id: TabId;
  labelKey: string;
  icon: typeof Settings;
}

const tabs: Tab[] = [
  { id: "settings", labelKey: "adminProject.tabs.settings", icon: Settings },
  { id: "collaborators", labelKey: "adminProject.tabs.collaborators", icon: Users },
];

export function AdminProjectDetail() {
  const { t } = useTranslation();
  const { workspaceId, projectId } = useParams<{ workspaceId: string; projectId: string }>();
  const navigate = useNavigate();
  const { isAdmin, isLoading: isProfileLoading } = useProfile();
  const { formatDate } = useDateFormatter();
  const [activeTab, setActiveTab] = useState<TabId>("settings");

  // Redirect non-admins to dashboard
  if (!isProfileLoading && !isAdmin) {
    return <Navigate to={publicRoutes.home} replace />;
  }

  const { data: project, isLoading, error } = useProject(projectId, workspaceId);

  if (isLoading) {
    return (
      <div>
        <PageHeader
          title={t("common.loading")}
          isAdmin
          breadcrumbs={[
            { label: t("nav.dashboard"), href: publicRoutes.home },
            { label: t("nav.adminSection") },
            { label: t("admin.workspaces"), href: "/admin/workspaces" },
            { label: t("adminProject.workspace"), href: `/admin/workspaces/${workspaceId}` },
            { label: "..." },
          ]}
        />
        <div className="space-y-4">
          <div className="h-12 bg-muted rounded-lg animate-pulse" />
          <div className="h-64 bg-muted rounded-lg animate-pulse" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <PageHeader
          title={t("common.error")}
          isAdmin
          breadcrumbs={[
            { label: t("nav.dashboard"), href: publicRoutes.home },
            { label: t("nav.adminSection") },
            { label: t("admin.workspaces"), href: "/admin/workspaces" },
            { label: t("adminProject.workspace"), href: `/admin/workspaces/${workspaceId}` },
            { label: t("common.error") },
          ]}
        />
        <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg">
          <AlertCircle className="h-5 w-5 text-red-600 shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-800 dark:text-red-200">{t("adminProject.loadError")}</p>
            <p className="text-xs text-red-600">{error.message}</p>
          </div>
          <button
            onClick={() => navigate(`/admin/workspaces/${workspaceId}`)}
            className="px-3 py-1.5 text-sm text-red-700 dark:text-red-300 border border-red-300 rounded-lg hover:bg-red-100 transition-colors"
          >
            {t("adminProject.backToWorkspace")}
          </button>
        </div>
      </div>
    );
  }

  if (!project) {
    return <Navigate to={`/admin/workspaces/${workspaceId}`} replace />;
  }

  return (
    <div>
      <PageHeader
        title={project.name}
        description={project.description}
        isAdmin
        breadcrumbs={[
          { label: t("nav.dashboard"), href: publicRoutes.home },
          { label: t("nav.adminSection") },
          { label: t("admin.workspaces"), href: "/admin/workspaces" },
          { label: t("adminProject.workspace"), href: `/admin/workspaces/${workspaceId}` },
          { label: project.name },
        ]}
        actions={
          <button
            onClick={() => navigate(`/admin/workspaces/${workspaceId}`)}
            className="inline-flex items-center gap-2 px-3 py-2 text-foreground border border-border rounded-lg hover:bg-accent transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            {t("common.back")}
          </button>
        }
      />

      {/* Tabs */}
      <div className="mb-6">
        {/* Mobile: Dropdown */}
        <div className="sm:hidden">
          <select
            value={activeTab}
            onChange={(e) => setActiveTab(e.target.value as TabId)}
            className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {tabs.map((tab) => (
              <option key={tab.id} value={tab.id}>
                {t(tab.labelKey)}
              </option>
            ))}
          </select>
        </div>

        {/* Desktop: Tab bar */}
        <div className="hidden sm:block border-b border-border">
          <nav className="flex space-x-4" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                    isActive
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {t(tab.labelKey)}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="bg-card rounded-lg border border-border p-6">
        {activeTab === "settings" && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-foreground mb-4">{t("adminProject.projectInformation")}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">{t("adminProject.name")}</p>
                  <p className="font-medium text-foreground">{project.name}</p>
                </div>
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">{t("adminProject.description")}</p>
                  <p className="font-medium text-foreground">{project.description || "—"}</p>
                </div>
                <div className="p-4 bg-muted rounded-lg flex items-center gap-3">
                  <FileText className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">{t("adminProject.documents")}</p>
                    <p className="font-medium text-foreground">{project.document_count ?? 0}</p>
                  </div>
                </div>
                <div className="p-4 bg-muted rounded-lg flex items-center gap-3">
                  <Users className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">{t("adminProject.collaborators")}</p>
                    <p className="font-medium text-foreground">{project.collaborators_count ?? 0}</p>
                  </div>
                </div>
                <div className="p-4 bg-muted rounded-lg flex items-center gap-3">
                  <Calendar className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">{t("adminProject.created")}</p>
                    <p className="font-medium text-foreground">{formatDate(project.created_at, "long-datetime")}</p>
                  </div>
                </div>
                <div className="p-4 bg-muted rounded-lg flex items-center gap-3">
                  <Calendar className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">{t("adminProject.lastUpdated")}</p>
                    <p className="font-medium text-foreground">{formatDate(project.updated_at, "long-datetime")}</p>
                  </div>
                </div>
              </div>
            </div>

            {project.instructions && (
              <div>
                <h3 className="text-lg font-medium text-foreground mb-4">{t("adminProject.instructions")}</h3>
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-foreground whitespace-pre-wrap">{project.instructions}</p>
                </div>
              </div>
            )}
          </div>
        )}
        {activeTab === "collaborators" && workspaceId && projectId && (
          <AdminProjectCollaboratorsList workspaceId={workspaceId} projectId={projectId} />
        )}
      </div>
    </div>
  );
}
